<template>
  <div>
    <app-template />
    <div class="uk-container uk-container-large">
      <h1>Match reports</h1>
      <div class="team-select-wrapper" style="margin-bottom: 1rem">
        <label for="clubTeamSelect"><strong>Select Team:</strong></label>
        <select id="clubTeamSelect" @change="loadSelectedTeamGames" v-model="selectedClubTeamId">
          <option disabled value="">Please select a team</option>
          <option v-for="team in clubTeams" :key="team.id" :value="team.id">
            {{ team.teamName }}
          </option>
        </select>
      </div>

      <div v-if="selectedClubTeamId" class="date-picker-wrapper" style="margin-bottom: 1rem">
        <label for="gameDateFrom"><strong>Load Games From:</strong></label>
        <VueDatePicker :locale="'en-GB'" v-model="selectedDateFrom" @update:model-value="onDateChange"
          :enable-time-picker="false" :max-date="maxDate" placeholder="Select start date" format="dd/MM/yyyy"
          auto-apply />
        <p class="date-helper-text">
          Select how far back to load match reports (up to today's date)
        </p>
      </div>
      <div class="tabs-wrapper">
        <section class="tab-content">
          <div v-if="selectedGamesEvents.length > 0" class="games-section">
            <div class="section-header">
              <h2 class="section-title">Games and Events for Selected Team</h2>
              <div class="games-count">
                {{ selectedGamesEvents.length }} games
              </div>
            </div>
            <div class="games-grid">
              <div v-for="event in selectedGamesEvents" :key="event.id" class="game-card">
                <div class="game-card-header">
                  <div class="game-type-badge" :class="getGameTypeBadgeClass(event.gameType)">
                    {{ event.gameType }}
                  </div>
                  <div class="game-status-indicator" :class="getGameStatusClass(event)">
                    <i class="bi" :class="getGameStatusIcon(event)"></i>
                  </div>
                </div>

                <div class="match-details">
                  <div v-if="event.result_added == '0'" class="teams-matchup">
                    <div class="team home-team">{{ event.teamName }}</div>
                    <div class="vs-separator">
                      <span class="vs-text">VS</span>
                    </div>
                    <div class="team away-team">{{ event.opposition }}</div>
                  </div>
                  <div v-else class="teams-result">
                    <div class="result-display">
                      {{ event.teamName }} {{ event.home_team_goals }} -
                      {{ event.away_team_goals }} {{ event.opposition }}
                    </div>
                  </div>

                  <div class="game-info">
                    <div class="info-item">
                      <i class="bi bi-calendar3"></i>
                      <span>{{ getDayAndDate(event.matchDate) }}</span>
                    </div>
                    <div class="info-item">
                      <i class="bi bi-clock"></i>
                      <span>{{
                        formatISOStringDateAndTime(event.kickOffTime)
                      }}</span>
                    </div>
                  </div>
                </div>

                <div class="game-card-footer">
                  <button @click="showMatchReports(event)" class="reports-button" v-b-tooltip.hover
                    title="View match reports">
                    <i class="bi bi-file-text" aria-hidden="true"></i>
                    <span>View Reports</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <div class="empty-state-icon">
              <i class="bi bi-calendar-x"></i>
            </div>
            <div class="empty-state-content">
              <h3>No Games Available</h3>
              <p>No games or events are available for the selected team.</p>
            </div>
          </div>
        </section>
      </div>
    </div>

    <b-modal id="allMatchReportsModal" v-if="allMatchReportsModal" v-model="allMatchReportsModal" size="xl"
      title="Match Reports Overview" hide-footer>

      <!-- Match Header Section -->
      <div class="match-header-section">
        <div class="match-header-teams">
          <div class="match-header-team home">
            <span class="team-name">{{ selectedGame.teamName }}</span>
          </div>

          <!-- Score Display (if result added) -->
          <div v-if="selectedGame.result_added == '1'" class="match-header-score">
            <div class="score-display">
              <span class="score home-score">{{ selectedGame.home_team_goals }}</span>
              <span class="score-separator">-</span>
              <span class="score away-score">{{ selectedGame.away_team_goals }}</span>
            </div>
            <div class="match-result-badge" :class="getMatchResultClass()">
              {{ getMatchResultText() }}
            </div>
          </div>
          <div v-else class="match-header-vs">
            <span class="vs-text">VS</span>
          </div>

          <div class="match-header-team away">
            <span class="team-name">{{ selectedGame.opposition }}</span>
          </div>
        </div>

        <!-- Goal Scorers (if available) -->
        <div
          v-if="selectedGame.result_added == '1' && (selectedGame.home_team_goal_scorers || selectedGame.away_team_goal_scorers)"
          class="goal-scorers-section">
          <div class="goal-scorers home-scorers" v-if="selectedGame.home_team_goal_scorers">
            <i class="bi bi-trophy-fill"></i>
            <span>{{ selectedGame.home_team_goal_scorers }}</span>
          </div>
          <div class="goal-scorers away-scorers" v-if="selectedGame.away_team_goal_scorers">
            <i class="bi bi-trophy-fill"></i>
            <span>{{ selectedGame.away_team_goal_scorers }}</span>
          </div>
        </div>

        <!-- Match Info Row -->
        <div class="match-info-row">
          <div class="match-info-item">
            <i class="bi bi-calendar3"></i>
            <span>{{ getDayAndDate(selectedGame.matchDate) }}</span>
          </div>
          <div class="match-info-item">
            <i class="bi bi-clock"></i>
            <span>Kick-off: {{ formatISOStringDateAndTime(selectedGame.kickOffTime) }}</span>
          </div>
          <div class="match-info-item" v-if="selectedGame.locationName">
            <i class="bi bi-geo-alt-fill"></i>
            <span>{{ selectedGame.locationName }}</span>
          </div>
          <div class="match-info-item" v-if="selectedGame.pitchType">
            <i class="bi bi-square-fill"></i>
            <span>{{ selectedGame.pitchType }}</span>
          </div>
        </div>
      </div>

      <!-- Eyeball Match Data Section -->
      <div v-if="eyeballImportAvailable" class="eyeball-section">
        <div class="eyeball-section-header" @click="toggleEyeballSection">
          <h4>
            <i class="bi bi-camera-video-fill"></i>
            Eyeball Match Video &amp; Team Lines
          </h4>
          <i class="bi toggle-icon" :class="showEyeballSection ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <transition name="slide">
          <div v-show="showEyeballSection" class="eyeball-section-content">
            <div v-if="isLoadingEyeballData" class="text-center py-3">
              <span class="spinner-border spinner-border-sm" role="status"></span> Loading Eyeball data...
            </div>
            <div v-else-if="eyeBallSelectedGameData.video" class="eyeball-game-wrapper">
              <!-- Video Player -->
              <div class="eyeball-video-wrapper" v-if="eyeBallSelectedGameData.video?.url">
                <video ref="hlsVideoPlayer" controls width="100%" style="max-height: 400px"
                  :poster="eyeBallSelectedGameData.video?.poster">
                  Your browser does not support the video tag.
                </video>
              </div>

              <!-- Team Lines -->
              <div class="eyeball-team-lines" v-if="eyeBallSelectedGameData.homeGameTeam || eyeBallSelectedGameData.awayGameTeam">
                <ul class="nav nav-tabs mb-3">
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: eyeballTeamSheetTab === 'homeTeam-tab' }"
                      @click="eyeballTeamSheetTab = 'homeTeam-tab'" href="javascript:;">
                      {{ eyeBallSelectedGameData.homeGameTeam?.club?.name || 'Home' }}
                    </a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: eyeballTeamSheetTab === 'awayTeam-tab' }"
                      @click="eyeballTeamSheetTab = 'awayTeam-tab'" href="javascript:;">
                      {{ eyeBallSelectedGameData.awayGameTeam?.club?.name || 'Away' }}
                    </a>
                  </li>
                </ul>

                <!-- Home Team -->
                <div v-if="eyeballTeamSheetTab === 'homeTeam-tab'">
                  <div v-if="eyeBallSelectedGameData.homeGameTeam?.players?.length > 0">
                    <h5>Starting XI</h5>
                    <table class="table table-sm">
                      <thead><tr><th>#</th><th>Name</th><th>Position</th><th>Goals</th><th>Minutes</th></tr></thead>
                      <tbody>
                        <tr v-for="player in eyeBallSelectedGameData.homeGameTeam.players.filter(p => !p.isSubstitute)" :key="player.id">
                          <td>{{ player.numberSign }}</td><td>{{ player.name }}</td><td>{{ player.positionKey }}</td>
                          <td>{{ player.goals }}</td><td>{{ formatMinutesFromMilliseconds(player.minutesPlayed) }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <h5>Substitutes</h5>
                    <table class="table table-sm">
                      <thead><tr><th>#</th><th>Name</th><th>Position</th><th>Goals</th><th>Minutes</th></tr></thead>
                      <tbody>
                        <tr v-for="player in eyeBallSelectedGameData.homeGameTeam.players.filter(p => p.isSubstitute)" :key="player.id">
                          <td>{{ player.numberSign }}</td><td>{{ player.name }}</td><td>{{ player.positionKey }}</td>
                          <td>{{ player.goals }}</td><td>{{ formatMinutesFromMilliseconds(player.minutesPlayed) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p v-else>No home team line data</p>
                </div>

                <!-- Away Team -->
                <div v-if="eyeballTeamSheetTab === 'awayTeam-tab'">
                  <div v-if="eyeBallSelectedGameData.awayGameTeam?.players?.length > 0">
                    <h5>Starting XI</h5>
                    <table class="table table-sm">
                      <thead><tr><th>#</th><th>Name</th><th>Position</th><th>Goals</th><th>Minutes</th></tr></thead>
                      <tbody>
                        <tr v-for="player in eyeBallSelectedGameData.awayGameTeam.players.filter(p => !p.isSubstitute)" :key="player.id">
                          <td>{{ player.numberSign }}</td><td>{{ player.name }}</td><td>{{ player.positionKey }}</td>
                          <td>{{ player.goals }}</td><td>{{ formatMinutesFromMilliseconds(player.minutesPlayed) }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <h5>Substitutes</h5>
                    <table class="table table-sm">
                      <thead><tr><th>#</th><th>Name</th><th>Position</th><th>Goals</th><th>Minutes</th></tr></thead>
                      <tbody>
                        <tr v-for="player in eyeBallSelectedGameData.awayGameTeam.players.filter(p => p.isSubstitute)" :key="player.id">
                          <td>{{ player.numberSign }}</td><td>{{ player.name }}</td><td>{{ player.positionKey }}</td>
                          <td>{{ player.goals }}</td><td>{{ formatMinutesFromMilliseconds(player.minutesPlayed) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p v-else>No away team line data</p>
                </div>
              </div>

              <!-- Import Button -->
              <div class="eyeball-import-section mt-3">
                <button @click="importEyeballDataToCoachReports()" class="eyeball-import-btn" :disabled="isImportingEyeballData || $store.getters.isReadOnlySeason">
                  <span v-if="isImportingEyeballData" class="spinner-border spinner-border-sm" role="status"></span>
                  <i v-else class="bi bi-download"></i>
                  <span class="btn-text">
                    <span class="btn-title">{{ isImportingEyeballData ? 'Importing…' : 'Import from Eyeball' }}</span>
                    <span class="btn-subtitle">Minutes, goals &amp; positions · won't overwrite existing data</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Team Sheet Section (Collapsible) -->
      <div class="team-sheet-section">
        <div class="team-sheet-header" @click="toggleTeamSheet">
          <h4>
            <i class="bi bi-people-fill"></i>
            Team Sheet
            <span class="player-count-badge">{{ getAttendingPlayersCount() }} players</span>
          </h4>
          <i class="bi toggle-icon" :class="isTeamSheetExpanded ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <transition name="slide">
          <div v-show="isTeamSheetExpanded" class="team-sheet-content">
            <div v-if="getAttendingPlayers().length > 0" class="team-sheet-grid">
              <div v-for="player in getAttendingPlayers()" :key="player.user_id" class="team-sheet-player">
                <div class="player-avatar">
                  <img v-if="getPlayersImage(player.user_id)" :src="getImgUrl(getPlayersImage(player.user_id))"
                    :alt="player.name" />
                  <i v-else class="bi bi-person-circle"></i>
                </div>
                <div class="player-info">
                  <span class="player-name">{{ player.name }}</span>
                  <span class="player-position" v-if="player.position">{{ player.position }}</span>
                </div>
              </div>
            </div>
            <div v-else class="team-sheet-empty">
              <i class="bi bi-person-x"></i>
              <p>No players confirmed for this match</p>
            </div>
          </div>
        </transition>
      </div>

      <!-- Tab Navigation -->
      <div class="modal-tabs-wrapper">
        <ul class="modal-tabs">
          <li class="modal-tab-item">
            <a :class="modalSelectedTab === 'complete-reports'
              ? 'modal-tab-link active'
              : 'modal-tab-link'
              " @click="changeModalTab('complete-reports')" href="javascript:;">
              <i class="bi bi-check-circle-fill"></i>
              Complete Reports
              <span class="tab-badge complete">
                {{
                  playersAndCoachReports.filter(
                    (item) =>
                      item.isPlayerReportComplete && item.isCoachReportComplete,
                  ).length
                }}
              </span>
            </a>
          </li>
          <li class="modal-tab-item">
            <a :class="modalSelectedTab === 'incomplete-reports'
              ? 'modal-tab-link active'
              : 'modal-tab-link'
              " @click="changeModalTab('incomplete-reports')" href="javascript:;">
              <i class="bi bi-exclamation-triangle-fill"></i>
              Incomplete Reports
              <span class="tab-badge incomplete">
                {{
                  playersAndCoachReports.filter(
                    (item) =>
                      !item.isPlayerReportComplete ||
                      !item.isCoachReportComplete,
                  ).length
                }}
              </span>
            </a>
          </li>
        </ul>

        <!-- Tab Content -->
        <div class="modal-tab-content">
          <!-- Complete Reports Tab -->
          <div v-if="modalSelectedTab === 'complete-reports'" class="tab-pane">
            <div class="report-category complete-reports" v-if="
              playersAndCoachReports.filter(
                (item) =>
                  item.isPlayerReportComplete && item.isCoachReportComplete,
              ).length > 0
            ">
              <ul>
                <li v-for="item in playersAndCoachReports.filter(
                  (item) =>
                    item.isPlayerReportComplete && item.isCoachReportComplete,
                )" :key="item.player.id + '-' + eyeballImportKey" class="player-item complete">
                  <span class="player-name">{{ item.player.name }}</span>
                  <span class="report-status">
                    <i class="bi bi-check-circle text-success"></i>
                    Player Report Complete
                  </span>
                  <span class="report-status">
                    <i class="bi bi-check-circle text-success"></i>
                    Coach Report Complete
                  </span>

                  <PlayerGameReport :item="item" :selectedGame="selectedGame"
                    :selectedGameReport="selectedMatchReportData" :userData="userData"
                    :assessmentAreas="assessmentAreas" :isPlayerReportComplete="item.isPlayerReportComplete"
                    :getPlayersImage="getPlayersImage" @open-coach-modal="handleOpenCoachModal"
                    :coachesData="coachesData" />
                </li>
              </ul>
            </div>
            <div v-else class="tab-empty-state">
              <div class="empty-state-icon">
                <i class="bi bi-check-circle"></i>
              </div>
              <div class="empty-state-content">
                <h4>No Complete Reports</h4>
                <p>
                  No players have both player and coach reports completed yet.
                </p>
              </div>
            </div>
          </div>

          <!-- Incomplete Reports Tab -->
          <div v-if="modalSelectedTab === 'incomplete-reports'" class="tab-pane">
            <div class="report-category incomplete-reports" v-if="
              playersAndCoachReports.filter(
                (item) =>
                  !item.isPlayerReportComplete || !item.isCoachReportComplete,
              ).length > 0
            ">
              <ul>
                <li v-for="item in playersAndCoachReports.filter(
                  (item) =>
                    !item.isPlayerReportComplete ||
                    !item.isCoachReportComplete,
                )" :key="item.player.id + '-' + eyeballImportKey" class="player-item incomplete">
                  <span class="player-name">{{ item.player.name }}</span>

                  <span class="report-status incomplete" v-if="!item.isPlayerReportComplete">
                    <i class="bi bi-clock text-warning"></i>
                    Player Report Not Complete
                  </span>
                  <span class="report-status complete" v-else>
                    <i class="bi bi-check-circle text-success"></i>
                    Player Report Complete
                  </span>

                  <span class="report-status incomplete" v-if="!item.isCoachReportComplete">
                    <i class="bi bi-clock text-warning"></i>
                    Coach Report Not Complete
                  </span>
                  <span class="report-status complete" v-else>
                    <i class="bi bi-check-circle text-success"></i>
                    Coach Report Complete
                  </span>

                  <PlayerGameReport :item="item" :selectedGame="selectedGame"
                    :selectedGameReport="selectedMatchReportData" :userData="userData"
                    :assessmentAreas="assessmentAreas" :isPlayerReportComplete="item.isPlayerReportComplete"
                    :getPlayersImage="getPlayersImage" @open-coach-modal="handleOpenCoachModal"
                    :coachesData="coachesData" />
                </li>
              </ul>
            </div>
            <div v-else class="tab-empty-state">
              <div class="empty-state-icon">
                <i class="bi bi-exclamation-triangle"></i>
              </div>
              <div class="empty-state-content">
                <h4>All Reports Complete!</h4>
                <p>Great job! All players have completed their reports.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoadingMatchReports" class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading match reports...</span>
        </div>
        <p>Loading match reports...</p>
      </div>

      <!-- Empty State for No Players -->
      <div v-if="!isLoadingMatchReports && selectedGameAttendees.length === 0" class="empty-state">
        <i class="bi bi-file-earmark-text" style="font-size: 3rem; color: #ccc"></i>
        <p>No players found for this match.</p>
      </div>
    </b-modal>

    <!-- Coach Game Performance Modal -->
    <b-modal v-if="showCoachGamePerformanceModal" v-model="showCoachGamePerformanceModal"
      title="Record coaches match report assessment" size="lg" :hide-footer="true">
      <form class="form" @submit.prevent="saveCoachGamePerformanceReport()">
        <div class="coach-modal-player-info">
          <img v-if="selectedPlayerForCoachReport.userImage" class="player-avatar-small"
            :src="getPlayerImageUrl(selectedPlayerForCoachReport.userImage)" alt="Player" />
          <img v-else class="player-avatar-small" :src="avatarPlaceholder" alt="Player" />
          <div class="player-info-text">
            <span class="player-name-modal">{{ selectedPlayerForCoachReport.name }}</span>
            <span class="match-info-modal">Match report for {{ formatDateForHuman(selectedGame.matchDate) }} vs {{
              selectedGame.opposition }}</span>
          </div>
        </div>

        <div v-if="eyeballPrefillApplied" class="alert alert-info py-2 px-3 mb-3" style="font-size: 0.9em;">
          <i class="bi bi-info-circle"></i>
          Pre-filled minutes, goals and positions from Eyeball team lines (won't overwrite existing data).
        </div>

        <div class="form__item">
          <label class="form__label" for="postion_played">What position did they play?</label>
          <select id="postion_played" v-model="coachReportFormData.postion_played" class="form-select">
            <option disabled value="">Please select a position</option>
            <option value="Goalkeeper">Goalkeeper</option>
            <option value="Right Full-back (Wingback)">Right Full-back (Wingback)</option>
            <option value="Left Full-back (Wingback)">Left Full-back (Wingback)</option>
            <option value="Center-back left">Center-back left</option>
            <option value="Center-back right">Center-back right</option>
            <option value="Centre midfield">Centre midfield</option>
            <option value="Centre Defensive Midfield">Centre Defensive Midfield</option>
            <option value="Defensive Midfielder">Defensive Midfielder</option>
            <option value="Right Midfielder (Winger)">Right Midfielder (Winger)</option>
            <option value="Left Midfielder (Winger)">Left Midfielder (Winger)</option>
            <option value="Attacking Midfielder">Attacking Midfielder</option>
            <option value="Center Forward (Striker) left">Center Forward (Striker) left</option>
            <option value="Center Forward (Striker) right">Center Forward (Striker) right</option>
          </select>
        </div>

        <div class="form__item">
          <label class="form__label" for="postion_played_two">Second position played?</label>
          <select id="postion_played_two" v-model="coachReportFormData.postion_played_two" class="form-select">
            <option value="">None</option>
            <option value="Goalkeeper">Goalkeeper</option>
            <option value="Right Full-back (Wingback)">Right Full-back (Wingback)</option>
            <option value="Left Full-back (Wingback)">Left Full-back (Wingback)</option>
            <option value="Center-back left">Center-back left</option>
            <option value="Center-back right">Center-back right</option>
            <option value="Centre midfield">Centre midfield</option>
            <option value="Centre Defensive Midfield">Centre Defensive Midfield</option>
            <option value="Defensive Midfielder">Defensive Midfielder</option>
            <option value="Right Midfielder (Winger)">Right Midfielder (Winger)</option>
            <option value="Left Midfielder (Winger)">Left Midfielder (Winger)</option>
            <option value="Attacking Midfielder">Attacking Midfielder</option>
            <option value="Center Forward (Striker) left">Center Forward (Striker) left</option>
            <option value="Center Forward (Striker) right">Center Forward (Striker) right</option>
          </select>
        </div>

        <div class="coach-stats-row">
          <div class="form__item">
            <label class="form__label" for="session_mins">Playing Time (mins)</label>
            <b-form-input id="session_mins" v-model="coachReportFormData.session_mins" type="number" min="0"
              placeholder="0"></b-form-input>
          </div>

          <div class="form__item">
            <label class="form__label" for="assists">Assists</label>
            <b-form-input id="assists" v-model="coachReportFormData.assists" type="number" min="0"
              placeholder="0"></b-form-input>
          </div>

          <div class="form__item">
            <label class="form__label" for="goals">Goals</label>
            <b-form-input id="goals" v-model="coachReportFormData.goals" type="number" min="0"
              placeholder="0"></b-form-input>
          </div>

          <div class="form__item">
            <label class="form__label">Cards</label>
            <div class="card-checkboxes">
              <b-form-checkbox v-model="coachReportFormData.yellow_card" :value="true" :unchecked-value="false"
                class="yellow-card-checkbox">
                <span class="card-icon yellow">🟨</span> Yellow
              </b-form-checkbox>
              <b-form-checkbox v-model="coachReportFormData.red_card" :value="true" :unchecked-value="false"
                class="red-card-checkbox">
                <span class="card-icon red">🟥</span> Red
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="form__item">
          <label class="form__label" for="player_rating">How did you think {{ selectedPlayerForCoachReport.name }}
            played?</label>
          <select id="player_rating" v-model="coachReportFormData.player_rating" class="form-select">
            <option value="">Please select a rating</option>
            <option value="Excellent">Excellent</option>
            <option value="Good">Good</option>
            <option value="Progressing well">Progressing well</option>
            <option value="⁠⁠Requires improvement">⁠⁠Requires improvement</option>
            <option value="Unsatisfactory">Unsatisfactory</option>
          </select>
        </div>

        <div class="form__item">
          <label class="form__label" for="game_comments">Positive comments</label>
          <CoachReportAIHelper
            v-model="coachReportFormData.game_comments"
            field-type="positives"
            :player-name="coachReportFormData.player_name || ''"
            :position="coachReportFormData.postion_played || ''" />
          <textarea class="form-control" id="game_comments" rows="3"
            v-model="coachReportFormData.game_comments"></textarea>
        </div>

        <div class="form__item">
          <label class="form__label" for="game_improvements">Improvements comments</label>
          <CoachReportAIHelper
            v-model="coachReportFormData.game_improvements"
            field-type="improvements"
            :player-name="coachReportFormData.player_name || ''"
            :position="coachReportFormData.postion_played || ''" />
          <textarea class="form-control" id="game_improvements" rows="3"
            v-model="coachReportFormData.game_improvements"></textarea>
        </div>

        <div class="form__item" v-if="coachesData.length > 0">
          <label class="form__label" for="coachSelect">Name of Coach completing the report</label>
          <b-form-select v-model="coachReportFormData.coachId" id="coachSelect" required>
            <b-form-select-option disabled value="">Please select a coach</b-form-select-option>
            <b-form-select-option v-for="coach in coachesData" :value="coach.id" :key="coach.id">
              {{ coach.name }}
            </b-form-select-option>
          </b-form-select>
        </div>

        <div class="form-actions">
          <b-button type="submit" variant="success" :disabled="isSavingCoachReport || $store.getters.isReadOnlySeason">
            <i class="bi bi-check-circle"></i>
            {{ isSavingCoachReport ? 'Saving...' : 'Submit Coach Assessment' }}
          </b-button>
          <b-button variant="secondary" @click="showCoachGamePerformanceModal = false">
            Cancel
          </b-button>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script lang="js">
import AppTemplate from "../layouts/App";
import PlayerGameReport from "@/components/PlayerGameReport.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { Teams } from "@/services/teams-service.js";
import { Users } from "@/services/user-service.js";
import { Games } from "@/services/games-service.js";
import { Coaches } from "@/services/coaches-service.js";
import { EyeBall } from "@/services/eyeball-service.js";
import CoachReportAIHelper from "@/components/CoachReportAIHelper.vue";
import Hls from "hls.js";
import extendSeasonEndDateToFullYearMixin from "@/mixin/extendSeasonEndDateToFullYear.js";
import { mapGetters } from "vuex";
import { CHECK_PROFILE_HAS_DATA } from "@/store/actions/user.js";
import { avatarUrl, AVATAR_PLACEHOLDER } from "@/utils/avatar";
export default {
  name: "MatchReports",
  components: { AppTemplate, PlayerGameReport, VueDatePicker, CoachReportAIHelper },
  mixins: [extendSeasonEndDateToFullYearMixin],
  data() {
    return {
      userData: {
        name: "",
        userType: "",
      },
      coachesData: [],
      clubTeams: [],
      selectedClubTeamId: "",
      selectedDateFrom: null,
      maxDate: new Date(),
      selectedGamesEvents: [],
      selectedGame: {},
      thisWeeksGames: [],
      pastGames: [],
      isLoadingMatchReports: false,
      playersAndCoachReports: [],
      playersWithCompleteReports: [],
      playersWithIncompleteReports: [],
      playersWithMissingReports: [],
      coachesMatchReports: [],
      selectedGameAttendees: [],
      selectedMatchReportData: {},
      selectedTeam: {},
      allGameAnalysisData: [],
      allMatchReportsModal: false,
      modalSelectedTab: "complete-reports",
      isTeamSheetExpanded: false,
      currentTeam: [],
      assessmentAreas: [
        {
          title: "Technical",
          improvementsKey: "technically_improvements",
          positivesKey: "technically_positives",
        },
        {
          title: "Tactical",
          improvementsKey: "tactility_improvements",
          positivesKey: "tactility_positives",
        },
        {
          title: "Physical",
          improvementsKey: "physicality_improvements",
          positivesKey: "physicality_positives",
        },
        {
          title: "Mental",
          improvementsKey: "mentally_improvements",
          positivesKey: "mentally_positives",
        },
      ],
      // Coach Game Performance Modal
      showCoachGamePerformanceModal: false,
      eyeballPrefillApplied: false,
      selectedPlayerForCoachReport: {},
      coachReportFormData: {
        postion_played: '',
        postion_played_two: '',
        session_mins: 0,
        assists: 0,
        goals: 0,
        yellow_card: false,
        red_card: false,
        player_rating: '',
        game_comments: '',
        game_improvements: '',
        coachId: '',
      },
      isSavingCoachReport: false,
      eyeballImportKey: 0,
      // Eyeball integration
      eyeBallTeamsGames: [],
      eyeBallSelectedGameData: {},
      showEyeballSection: false,
      isLoadingEyeballData: false,
      isImportingEyeballData: false,
      eyeballImportAvailable: false,
      eyeballTeamSheetTab: "homeTeam-tab",
      hlsInstance: null,
    };
  },
  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(["getProfile"]),
    avatarPlaceholder() {
      return AVATAR_PLACEHOLDER;
    },
  },
  created() {
    this.$store.dispatch(CHECK_PROFILE_HAS_DATA).then((response) => {
      if (response === true) {
        this.setUser();
        this.getUserById(this.userData.id);
        this.getAllCoaches();
      }
    });
  },
  methods: {
    async getAllCoaches() {
      const userData = {
        action: "getAllCoachesByClubId",
        clubId: this.userData.clubId,
      };

      const responce = await Coaches.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.coachesData = responce.data;
      }
    },
    getImgUrl(pic) {
      // Empty stays empty (callers rely on rendering nothing); a stored
      // "default-image.png" resolves to the neutral avatar via avatarUrl.
      return pic !== undefined && pic !== "" ? avatarUrl(pic) : "";
    },
    getPlayersImage(playerId) {
      const player = this.currentTeam.find((player) => player.id === playerId);
      if (
        player &&
        player.userImage &&
        player.userImage !== null &&
        player.userImage !== "" &&
        player.userImage !== undefined
      ) {
        return player.userImage;
      }
      return null;
    },
    async fetchPlayerMatchReports(matchReport) {
      if (!matchReport || !matchReport.id) {
        console.warn("No selected game found for match reports");
        return;
      }

      this.isLoadingMatchReports = true;

      try {
        // Reset arrays
        this.playersWithCompleteReports = [];
        this.playersWithIncompleteReports = [];
        this.playersWithMissingReports = [];
        this.playersAndCoachReports = [];

        // Load match report data first
        await this.getMatchReportForSelectedGame(matchReport);
        await this.getCoachGamePerformanceReportsByGameId(matchReport.id);

        // Process each attendee to check their report status
        for (const attendee of this.selectedGameAttendees) {
          try {
            // Only check players who are going to the game
            if (attendee.going === 1) {
              // Find the match report for this specific player
              const playerReport =
                this.selectedMatchReportData.playerMatchReports.find(
                  (report) => report.player_id === attendee.user_id,
                );

              const playerData = {
                id: attendee.user_id,
                name: attendee.name,
                reportLength: playerReport ? 1 : 0,
              };

              const coachReport = this.coachesMatchReports.find(
                (report) => report.user_id === attendee.user_id,
              );

              if (playerReport) {
                // Check if the report is complete using your existing function
                const isPlayerReportComplete =
                  this.checkCoachPerformanceReportIsNotEmptyForPlayer(
                    playerReport,
                  );
                const isCoachReportComplete =
                  this.checkCoachPerformanceReportIsNotEmptyForCoach(
                    coachReport,
                  );

                playerData.coachReport = coachReport;

                this.playersAndCoachReports.push({
                  player: playerData,
                  playerReport: playerReport,
                  coachReport: coachReport,
                  isPlayerReportComplete: isPlayerReportComplete,
                  isCoachReportComplete: isCoachReportComplete,
                });

                if (isPlayerReportComplete) {
                  this.playersWithCompleteReports.push(playerData);
                } else {
                  this.playersWithIncompleteReports.push(playerData);
                }
              } else {
                // No report exists for this player
                this.playersWithMissingReports.push(playerData);
              }
            }
          } catch (error) {
            console.error(
              `Error checking report for player ${attendee.name}:`,
              error,
            );
            // Treat as missing report on error
            this.playersWithMissingReports.push({
              id: attendee.user_id,
              name: attendee.name,
              reportLength: 0,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching player match reports:", error);
        this.$toast.error("Failed to load match reports");
      } finally {
        this.isLoadingMatchReports = false;
      }
    },
    async getGameAnalysisData(gameData) {
      const data = {
        teamId: gameData.teamId,
        season_start_date: this.selectedTeam.season_start_date,
        season_end_date: this.selectedTeam.season_end_date,
        action: "getGameAnalysisData",
      };

      const responce = await Games.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.allGameAnalysisData = responce.data;
      }
    },
    async showMatchReports(matchReport) {
      this.selectedGame = matchReport;
      // Reset Eyeball state
      this.eyeBallSelectedGameData = {};
      this.showEyeballSection = false;
      this.destroyHLSPlayer();
      // Check Eyeball availability
      this.eyeballImportAvailable = this.checkEyeBallMatchData(matchReport) !== undefined;
      await this.fetchPlayerMatchReports(matchReport);
      this.allMatchReportsModal = true;
    },
    populategameAnalysisMatchReportData(id) {
      const gameAnalysisData = this.getgameAnalysisByGameId(id);
      return gameAnalysisData !== undefined
        ? gameAnalysisData
        : { generalComments: "", physio_name: "" };
    },
    getgameAnalysisByGameId(gameId) {
      return this.allGameAnalysisData.find(
        (gameAnalysis) => gameAnalysis.gameId === gameId,
      );
    },
    makeMatchReportDataObject(gameData, allPlayerMatchReports) {
      const gameAnalysisData = this.populategameAnalysisMatchReportData(
        gameData.id,
      );
      this.selectedMatchReportData = {
        fullGameData: gameData,
        opposition: gameData.opposition,
        teamName: gameData.teamName,
        matchDate: gameData.matchDate,
        home_team: gameData.teamName,
        result_added: gameData.result_added,
        away_team_goals: gameData.away_team_goals,
        home_team_goals: gameData.home_team_goals,
        generalComments: gameAnalysisData.generalComments,
        physio_name: gameAnalysisData.physio_name,
        coachId: "",
        playerMatchReports: [],
      };

      this.selectedMatchReportData.playerMatchReports.push(
        allPlayerMatchReports,
      );

      /*this.selectedGameAttendees.map((player) => {
        if (player.going === 1) {

          const playerMatchReport = {
            player_id: player.user_id,
            age_group: player.age_group,
            positions_played: [player.position],
            performance_rating: 0,
            game_comments: "",
            game_improvements: "",
            players_name: player.name,
            gameId: gameData.id,
            session_mins: 0,
            trialistId: null,
            assists: 0,
            goals: 0,
          };

          this.selectedMatchReportData.playerMatchReports.push(
            playerMatchReport,
          );
        }
      });*/
    },
    getPlayerData(playerId) {
      return this.currentTeam.find((player) => player.id === playerId);
    },
    async getCoachGamePerformanceReportsByGameId(gameId) {
      const userData = {
        gameId: gameId,
        under16: this.showIfUnderU16(this.selectedTeam.age_group),
        season_start_date: localStorage.getItem(
          "currentPlayingseasonStartDate",
        ),
        season_end_date: this.extendSeasonEndDateToFullYear(),
        action: "getCoachGamePerformanceReportsByGameId",
      };
      const response = await Games.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.status === 200) {
        this.coachesMatchReports = response.data;
      }
    },
    showIfUnderU16() {
      if (this.selectedUserData !== undefined) {
        const currentYear = new Date().getFullYear();
        const birthYear = parseInt(this.selectedUserData.age_group);

        // Check if birth year is valid
        if (isNaN(birthYear)) {
          return false;
        }

        // Calculate age based on current year
        const age = currentYear - birthYear;

        // Return true if player is under 14
        return age < 14;
      }
      return false;
    },
    async getMatchReportForSelectedGame(gameData) {
      const data = {
        action: "getMatchReportForSelectedGame",
        gameId: gameData.id,
      };

      const response = await Games.find(data).catch((error) => {
        console.log(error);
      });

      if (response.status === 200) {
        if (response.data.length === 0) {
          this.loadingStuff = true;
          await Promise.all([
            this.getCoachGamePerformanceReportsByGameId(gameData.id),
            this.getAllAttendeesForAGame(gameData),
            this.getGameAnalysisData(gameData),
            this.makeMatchReportDataObject(gameData, response.data),
          ]);
          this.loadingStuff = false;
        } else {
          await this.getAllAttendeesForAGame(gameData);
          const gameAnalysisData =
            await this.populategameAnalysisMatchReportData(gameData.id);
          this.selectedMatchReportData = {
            fullGameData: gameData,
            opposition: gameData.opposition,
            teamName: gameData.teamName,
            matchDate: gameData.matchDate,
            home_team: gameData.teamName,
            result_added: gameData.result_added,
            away_team_goals: gameData.away_team_goals,
            home_team_goals: gameData.home_team_goals,
            generalComments: gameAnalysisData.generalComments,
            physio_name: gameAnalysisData.physio_name,
            coachId: this.selectedTeam.coach_id,
            playerMatchReports: [],
          };

          response.data.map((player) => {
            this.selectedMatchReportData.coachId = player.coachId;
            player.positions_played = player.positions_played.split(",");

            if (this.isStringNumberBetween0And5(player.performance_rating)) {
              player.performance_rating = this.changeNumbersToRating(
                player.performance_rating,
              );
            }

            // Ensure goals and assists are numbers (may come as strings from DB)
            player.goals = parseInt(player.goals) || 0;
            player.assists = parseInt(player.assists) || 0;

            // Ensure yellow_card and red_card are booleans (handle string "1" from DB)
            player.yellow_card =
              player.yellow_card === 1 ||
              player.yellow_card === "1" ||
              player.yellow_card === true;
            player.red_card =
              player.red_card === 1 ||
              player.red_card === "1" ||
              player.red_card === true;

            // Combine yellow_card and red_card into a single 'card' property for PlayerGameReport.vue
            if (player.red_card && player.yellow_card) {
              player.card = "Yellow + Red";
            } else if (player.red_card) {
              player.card = "Red";
            } else if (player.yellow_card) {
              player.card = "Yellow";
            } else {
              player.card = "None";
            }

            this.selectedMatchReportData.playerMatchReports.push(player);
          });

          await this.getCoachGamePerformanceReportsByGameId(gameData.id);
        }
      }
    },
    isStringNumberBetween0And5(str) {
      const num = Number(str);
      return !isNaN(num) && num >= 0 && num <= 5;
    },
    checkForInjuredPlayers(attendeeList) {
      return attendeeList;
    },
    async getAllAttendeesForAGame() {
      const userData = {
        userId: this.userData.id,
        kickOffTime: this.selectedGame.kickOffTime,
        teamId: this.selectedGame.teamId,
        action: "getAllAttendeesForAGame",
      };
      const responce = await Games.find(userData).catch((error) => {
        console.log(error);
      });
      this.selectedGameAttendees = this.checkForInjuredPlayers(responce.data);
      this.noResponsePlayers();
    },
    noResponsePlayers() {
      if (this.selectedGameAttendees.length == 0) {
        this.noResponselayersList = this.currentTeam;
      } else {
        this.noResponselayersList = this.currentTeam.filter((player) => {
          return !this.selectedGameAttendees.some(function (attendee) {
            return player.id == attendee.user_id;
          });
        });
      }
    },
    changeNumbersToRating(rating) {
      // Handle null, undefined, or empty values
      if (!rating || rating === null || rating === undefined) {
        console.log("Rating is null/undefined/empty:", rating);
        return "Progressing well";
      }

      // Convert to number for consistent comparison
      const numRating = Number(rating);

      // Handle both string and number ratings
      switch (numRating) {
        case 5:
        case "5":
          return "Excellent";
        case 4:
        case "4":
          return "Good";
        case 3:
        case "3":
          return "Progressing well";
        case 2:
        case "2":
          return "⁠⁠Requires improvement";
        case 1:
        case "1":
          return "Unsatisfactory";
        default:
          return "Progressing well"; // Default case if no match found
      }
    },
    checkCoachPerformanceReportIsNotEmptyForPlayer(session) {
      const requiredKeys = [
        "technically_improvements",
        "technically_positives",
        "tactility_improvements",
        "tactility_positives",
        "mentally_improvements",
        "mentally_positives",
        "physicality_improvements",
        "physicality_positives",
      ];
      return requiredKeys.some(
        (key) =>
          session[key] !== null &&
          session[key] !== "" &&
          session["player_self_rating"] !== "Not Rated",
      );
    },
    checkCoachPerformanceReportIsNotEmptyForCoach(session) {
      // Return false if session is null or undefined
      if (!session) {
        return false;
      }

      // Check if player_rating has a meaningful value (not empty, null, or "Not Rated"/"Not set")
      const hasValidRating = session.player_rating !== null &&
        session.player_rating !== "" &&
        session.player_rating !== "Not Rated" &&
        session.player_rating !== "Not set";

      // Check if at least one feedback field has meaningful content
      const hasPositiveFeedback = session.game_comments !== null &&
        session.game_comments !== "" &&
        session.game_comments !== "No positive feedback provided";

      const hasImprovementFeedback = session.game_improvements !== null &&
        session.game_improvements !== "" &&
        session.game_improvements !== "No improvement areas noted";

      // Coach assessment is complete only if there's a valid rating AND at least one feedback field
      return hasValidRating && (hasPositiveFeedback || hasImprovementFeedback);
    },
    filterGameEvents(allGameEventData) {
      // Helper function to sort by newest date first
      function sortByNewest(a, b) {
        const dateA = new Date(a.matchDate);
        const dateB = new Date(b.matchDate);
        return dateB - dateA; // Newest first
      }

      // Sort upcoming games by soonest first (ascending)
      const today = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
      this.thisWeeksGames = allGameEventData
        .filter((game) => {
          return game.gameType !== "Training" && game.matchDate >= today;
        })
        .sort((a, b) => new Date(a.matchDate) - new Date(b.matchDate));

      this.pastGames = allGameEventData
        .filter((game) => {
          return game.gameType !== "Training" && game.matchDate < today;
        })
        .sort(sortByNewest);

      this.selectedGamesEvents = [...this.thisWeeksGames, ...this.pastGames];
    },
    onDateChange() {
      // When date changes, reload games with the new date range
      if (this.selectedDateFrom && this.selectedClubTeamId) {
        const today = new Date();
        const gameFromDate = new Date(this.selectedDateFrom)
          .toISOString()
          .slice(0, 10);
        const gameToDate = today.toISOString().slice(0, 10);
        this.getSelectedTeamsEvents(gameFromDate, gameToDate);
      }
    },
    loadSelectedTeamGames() {
      this.selectedTeam = this.clubTeams.find(
        (team) => team.id === this.selectedClubTeamId,
      );
      this.getUserByTeamId();
      this.getEyeBallTeamsGames();

      // Set default date to 6 weeks ago if not already set
      if (!this.selectedDateFrom) {
        const today = new Date();
        const sixWeeksAgo = new Date();
        sixWeeksAgo.setDate(today.getDate() - 42); // 6 weeks = 42 days
        this.selectedDateFrom = sixWeeksAgo;
      }

      const today = new Date();
      let gameFromDate;
      let gameToDate;
      if (localStorage.getItem("selectedSeasonYear")) {
        // Browsing a past season: load that whole season, not the rolling
        // "6 weeks ago → today" default.
        gameFromDate = localStorage.getItem("currentPlayingseasonStartDate");
        gameToDate = localStorage.getItem("currentPlayingseasonEndDate");
      } else {
        gameFromDate = new Date(this.selectedDateFrom)
          .toISOString()
          .slice(0, 10);
        gameToDate = today.toISOString().slice(0, 10); // 'YYYY-MM-DD'
      }

      this.getSelectedTeamsEvents(gameFromDate, gameToDate);
    },
    async getUserByTeamId() {
      this.currentTeam = [];
      const userData = {
        teamId: this.selectedTeam.id,
        action: "getUserByTeamId",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.currentTeam = responce.data;
      }
    },
    async getSelectedTeamsEvents(startDate, endDate) {
      // Guard against junk dates (e.g. moment's "Invalid date") reaching the
      // backend, where new DateTime() would throw a fatal on PHP 8.3.
      const safeDate = (v) =>
        v && String(v).trim().toLowerCase() !== "invalid date" &&
        !isNaN(new Date(v).getTime())
          ? v
          : null;
      const data = {
        action: "getAllGamesEventsById",
        teamId: this.selectedClubTeamId,
        gameFromDate: safeDate(startDate),
        gameToDate: safeDate(endDate),
      };
      const responce = await Games.find(data).catch((error) => {
        console.log(error);
      });
      this.filterGameEvents(responce.data);
    },
    setUser() {
      if (this.getProfile !== null) {
        this.userData = this.getProfile;
      }
    },
    async getUserById(clientId) {
      const userData = {
        clientId: clientId,
        action: "getUserByID",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.userData = responce.data[0];
        this.getTeamsByUserId();
      }
    },
    async getTeamsByUserId() {
      const data = {
        action: "getTeamsDataById",
        userId: this.userData.id,
        clubId: this.userData.clubId,
      };
      const responce = await Teams.find(data).catch((error) => {
        console.log(error);
      });
      this.clubTeams = responce.data;
    },
    getDayAndDate(date) {
      // Vanilla JS: "Monday, September 29th 2025"
      const d = new Date(date);
      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const dayName = days[d.getDay()];
      const monthName = months[d.getMonth()];
      const day = d.getDate();
      const year = d.getFullYear();
      // Add ordinal suffix
      function ordinal(n) {
        const s = ["th", "st", "nd", "rd"],
          v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      }
      return `${dayName}, ${monthName} ${ordinal(day)} ${year}`;
    },
    formatISOStringDateAndTime(timeDateString) {
      // Vanilla JS: "h:mm am/pm" from ISO string
      const d = new Date(timeDateString);
      let hours = d.getUTCHours();
      const minutes = d.getUTCMinutes();
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      const mins = minutes < 10 ? `0${minutes}` : minutes;
      return `${hours}:${mins} ${ampm}`;
    },
    getGameTypeBadgeClass(gameType) {
      // Return CSS class based on game type
      const type = gameType.toLowerCase();
      if (type.includes("friendly")) return "friendly";
      if (type.includes("league")) return "league";
      if (type.includes("cup")) return "cup";
      if (type.includes("tournament")) return "tournament";
      return "default";
    },
    getGameStatusClass(event) {
      // Determine game status based on match date and result
      const matchDate = new Date(event.matchDate);
      const today = new Date();

      if (event.result_added === "1") {
        return "completed";
      } else if (matchDate < today) {
        return "in-progress";
      } else {
        return "upcoming";
      }
    },
    getGameStatusIcon(event) {
      // Return appropriate icon class based on game status
      const status = this.getGameStatusClass(event);

      switch (status) {
        case "completed":
          return "bi-check-circle-fill";
        case "in-progress":
          return "bi-play-circle-fill";
        case "upcoming":
          return "bi-clock-fill";
        default:
          return "bi-circle";
      }
    },
    changeModalTab(tabName) {
      this.modalSelectedTab = tabName;
    },
    toggleTeamSheet() {
      this.isTeamSheetExpanded = !this.isTeamSheetExpanded;
    },
    getAttendingPlayers() {
      return this.selectedGameAttendees.filter(player => player.going === 1);
    },
    getAttendingPlayersCount() {
      return this.getAttendingPlayers().length;
    },
    getMatchResultClass() {
      const homeGoals = parseInt(this.selectedGame.home_team_goals) || 0;
      const awayGoals = parseInt(this.selectedGame.away_team_goals) || 0;

      if (homeGoals > awayGoals) return 'win';
      if (homeGoals < awayGoals) return 'loss';
      return 'draw';
    },
    getMatchResultText() {
      const homeGoals = parseInt(this.selectedGame.home_team_goals) || 0;
      const awayGoals = parseInt(this.selectedGame.away_team_goals) || 0;

      if (homeGoals > awayGoals) return 'WIN';
      if (homeGoals < awayGoals) return 'LOSS';
      return 'DRAW';
    },
    formatDateForHuman(date) {
      if (!date) return "Invalid date";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "Invalid date";
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const monthName = months[d.getMonth()];
      const day = d.getDate();
      const year = d.getFullYear();
      // Ordinal suffix
      function ordinal(n) {
        const s = ["th", "st", "nd", "rd"],
          v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      }
      return `${monthName} ${ordinal(day)} ${year}`;
    },
    convertCoachRatingToWords(rating) {
      const ratingMap = {
        0: "No Rating",
        1: "Unsatisfactory",
        2: "⁠⁠Requires improvement",
        3: "Progressing well",
        4: "Good",
        5: "Excellent",
      };
      return ratingMap[rating] || rating;
    },
    handleOpenCoachModal(data) {
      // Handle the event emitted from PlayerGameReport component
      // data contains { report, mode, player, playerReport }
      this.openCoachGamePerformanceModal(data.report, data.mode, data.player, data.playerReport);
    },
    async openCoachGamePerformanceModal(report, mode, player, playerReport) {
      // Use the player data passed from the component
      if (player) {
        this.selectedPlayerForCoachReport = {
          id: player.id,
          name: player.name,
          userImage: this.getPlayersImage(player.id),
        };
      } else {
        // Fallback - try to get from the expanded report context
        this.selectedPlayerForCoachReport = {
          id: report.player_id || report.user_id,
          name: report.player_name || 'Player',
          userImage: null,
        };
      }

      // Find existing coach report for this player from loaded data
      const existingCoachReport = this.coachesMatchReports.find(
        (r) => (r.user_id === this.selectedPlayerForCoachReport.id || r.player_id === this.selectedPlayerForCoachReport.id),
      );

      // Check for Eyeball data for this player (use String comparison for ID matching)
      const selectedId = String(this.selectedPlayerForCoachReport.id);
      let playerItem = this.playersAndCoachReports.find(
        (item) => String(item.player.id) === selectedId,
      );
      let eyeballPlayerData = playerItem?.eyeballData || null;

      // Auto-fetch Eyeball data on first open if available but not yet loaded
      if (!eyeballPlayerData && this.eyeballImportAvailable && this.selectedGame?.matchDate) {
        try {
          const response = await EyeBall.find({
            action: "getEyeballDataForMatchReport",
            matchDate: this.selectedGame.matchDate,
            clubId: this.userData.clubId,
          });
          if (response?.data?.success) {
            const allEyeballData = response.data.playerData || {};
            // Cache onto all players so we don't refetch on next modal open
            this.playersAndCoachReports = this.playersAndCoachReports.map((item) => {
              const pid = String(item.player.id);
              return allEyeballData[pid]
                ? { ...item, eyeballData: allEyeballData[pid] }
                : item;
            });
            eyeballPlayerData = allEyeballData[selectedId] || null;
          }
        } catch (err) {
          console.error("Auto-fetch Eyeball data failed:", err);
        }
      }

      this.eyeballPrefillApplied = !!eyeballPlayerData;

      // Helper: check if a value is meaningfully set (not 0, "0", empty, null)
      const hasValue = (val) => val !== null && val !== undefined && val !== "" && val !== 0 && val !== "0";

      // Pre-populate form: existing coach data takes priority, then Eyeball, then default
      this.coachReportFormData = {
        postion_played: (hasValue(existingCoachReport?.positions_played) ? existingCoachReport.positions_played : null) || eyeballPlayerData?.position || '',
        postion_played_two: '',
        session_mins: (hasValue(existingCoachReport?.session_mins) ? existingCoachReport.session_mins : null) || eyeballPlayerData?.minutes || 0,
        assists: existingCoachReport?.assists || 0,
        goals: (hasValue(existingCoachReport?.goals) ? existingCoachReport.goals : null) || eyeballPlayerData?.goals || 0,
        yellow_card: existingCoachReport?.yellow_card === 1 || existingCoachReport?.yellow_card === '1' || existingCoachReport?.yellow_card === true || false,
        red_card: existingCoachReport?.red_card === 1 || existingCoachReport?.red_card === '1' || existingCoachReport?.red_card === true || false,
        player_rating: existingCoachReport?.player_rating || '',
        game_comments: existingCoachReport?.game_comments || '',
        game_improvements: existingCoachReport?.game_improvements || '',
        coachId: existingCoachReport?.coachId || this.userData.id || '',
        gameId: this.selectedGame.id,
        user_id: this.selectedPlayerForCoachReport.id,
      };

      // Load coaches data if not already loaded
      if (this.coachesData.length === 0) {
        await this.loadCoachesData();
      }

      this.showCoachGamePerformanceModal = true;
    },
    async loadCoachesData() {
      try {
        const response = await Users.find({
          clubId: this.userData.clubId,
          action: 'populateOnlyCoachesData',
        });
        if (response.data && response.data.length > 0) {
          this.coachesData = response.data;
        }
      } catch (error) {
        console.error('Error loading coaches data:', error);
      }
    },
    async saveCoachGamePerformanceReport() {
      this.isSavingCoachReport = true;
      try {
        const userData = {
          ...this.coachReportFormData,
          player_id: this.selectedPlayerForCoachReport.id,
          created_date: new Date().toISOString().split('T')[0],
          action: 'saveCoachesGamePerformanceReport',
        };

        const response = await Games.find(userData);

        if (response.status === 200) {
          this.$toast.success('Coach assessment saved successfully');
          this.showCoachGamePerformanceModal = false;

          // Refresh the match reports data
          await this.showMatchReports(this.selectedGame);
        } else {
          this.$toast.error('Failed to save coach assessment');
        }
      } catch (error) {
        console.error('Error saving coach report:', error);
        this.$toast.error('Error saving coach assessment');
      } finally {
        this.isSavingCoachReport = false;
      }
    },
    getPlayerImageUrl(image) {
      return avatarUrl(image);
    },

    // Eyeball Integration Methods
    getEyeballTeamId() {
      if (this.userData.clubId === "ptfc-2085XH") return 8733;
      if (this.userData.clubId === "stjfc-2085XH") return 61829;
      return null;
    },

    async getEyeBallTeamsGames() {
      const teamIdValue = this.getEyeballTeamId();
      if (!teamIdValue) return;

      try {
        const response = await EyeBall.find({
          action: "getEyeBallTeamsGames",
          teamId: teamIdValue,
        });

        if (response && response.status === 200) {
          this.eyeBallTeamsGames = response.data.games || [];
        }
      } catch (error) {
        console.error("Error fetching Eyeball games:", error);
      }
    },

    checkEyeBallMatchData(game) {
      if (this.eyeBallTeamsGames && this.eyeBallTeamsGames.length > 0) {
        return this.eyeBallTeamsGames.find((item) => item.date === game.matchDate);
      }
      return undefined;
    },

    async loadEyeballGameData() {
      const eyeballGame = this.checkEyeBallMatchData(this.selectedGame);
      if (!eyeballGame) return;

      this.isLoadingEyeballData = true;
      try {
        const response = await EyeBall.find({
          action: "getEyeBallGameById",
          gameId: eyeballGame.id,
        });

        if (response && response.status === 200) {
          this.eyeBallSelectedGameData = response.data;

          // Initialize HLS player after data loads
          this.$nextTick(() => {
            this.initializeHLSPlayer();
          });
        }
      } catch (error) {
        console.error("Error fetching Eyeball game data:", error);
      } finally {
        this.isLoadingEyeballData = false;
      }
    },

    toggleEyeballSection() {
      this.showEyeballSection = !this.showEyeballSection;
      if (this.showEyeballSection && !this.eyeBallSelectedGameData.video) {
        this.loadEyeballGameData();
      }
    },

    initializeHLSPlayer() {
      const video = this.$refs.hlsVideoPlayer;
      const videoUrl = this.eyeBallSelectedGameData.video?.url;
      const posterUrl = this.eyeBallSelectedGameData.video?.poster;

      if (!video || !videoUrl) return;

      if (posterUrl && !video.poster) {
        video.poster = posterUrl;
      }

      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(videoUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                hls.destroy();
                break;
            }
          }
        });
        this.hlsInstance = hls;
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = videoUrl;
      }
    },

    destroyHLSPlayer() {
      if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }
    },

    formatMinutesFromMilliseconds(milliseconds) {
      if (!milliseconds || milliseconds === 0) return "0:00";
      const totalMinutes = Math.floor(milliseconds / 60000);
      const seconds = Math.floor((milliseconds % 60000) / 1000);
      return `${totalMinutes}:${seconds.toString().padStart(2, "0")}`;
    },

    async importEyeballDataToCoachReports() {
      this.isImportingEyeballData = true;
      try {
        const response = await EyeBall.find({
          action: "getEyeballDataForMatchReport",
          matchDate: this.selectedGame.matchDate,
          clubId: this.userData.clubId,
        });

        if (!response || !response.data || !response.data.success) {
          this.$toast.error(response?.data?.message || "Failed to fetch Eyeball data");
          return;
        }

        const eyeballData = response.data.playerData || {};
        let importedCount = 0;

        // Update the playersAndCoachReports with Eyeball data
        this.playersAndCoachReports = this.playersAndCoachReports.map((item) => {
          const playerId = String(item.player.id);
          const data = eyeballData[playerId];
          if (!data) return item;

          const updatedReport = { ...item.playerReport };

          // Only pre-fill if current value is default/empty
          if (!updatedReport.session_mins || updatedReport.session_mins === 0 || updatedReport.session_mins === "0") {
            updatedReport.session_mins = data.minutes;
          }
          if (!updatedReport.goals || updatedReport.goals === 0 || updatedReport.goals === "0") {
            updatedReport.goals = data.goals;
          }
          if (!updatedReport.positions_played || updatedReport.positions_played.length === 0 ||
              (updatedReport.positions_played.length === 1 && updatedReport.positions_played[0] === "")) {
            if (data.position && data.position !== "") {
              updatedReport.positions_played = [data.position];
            }
          }

          importedCount++;
          return { ...item, playerReport: updatedReport, eyeballData: data };
        });

        // Also update selectedMatchReportData.playerMatchReports for consistency
        if (this.selectedMatchReportData.playerMatchReports) {
          this.selectedMatchReportData = {
            ...this.selectedMatchReportData,
            playerMatchReports: this.selectedMatchReportData.playerMatchReports.map((report) => {
              const playerId = String(report.player_id);
              const data = eyeballData[playerId];
              if (!data) return report;

              const updated = { ...report };
              if (!updated.session_mins || updated.session_mins === 0 || updated.session_mins === "0") {
                updated.session_mins = data.minutes;
              }
              if (!updated.goals || updated.goals === 0 || updated.goals === "0") {
                updated.goals = data.goals;
              }
              if (!updated.positions_played || updated.positions_played.length === 0 ||
                  (updated.positions_played.length === 1 && updated.positions_played[0] === "")) {
                if (data.position && data.position !== "") {
                  updated.positions_played = [data.position];
                }
              }
              return updated;
            }),
          };
        }

        // Force re-render of PlayerGameReport components
        this.eyeballImportKey++;

        const unlinkedCount = response.data.unlinkedCount || 0;
        this.$toast.success(
          `Imported Eyeball data for ${importedCount} player${importedCount !== 1 ? "s" : ""}.` +
          (unlinkedCount > 0 ? ` ${unlinkedCount} unlinked.` : "")
        );

        // Fire-and-forget: persist Eyeball minutes to player_stats as source-of-truth
        EyeBall.find({
          action: "persistEyeballMinutes",
          matchDate: this.selectedGame.matchDate,
          clubId: this.userData.clubId,
          gameId: this.selectedGame.id || null,
          teamId: this.selectedGame.teamId || null,
        }).catch((err) => console.error("persistEyeballMinutes failed:", err));
      } catch (error) {
        console.error("Error importing Eyeball data:", error);
        this.$toast.error("Failed to import Eyeball data");
      } finally {
        this.isImportingEyeballData = false;
      }
    },
  },
  beforeUnmount() {
    this.destroyHLSPlayer();
  },
};
</script>
<style scoped lang="scss">
h3.modal-title {
  text-transform: inherit;
}

.games-section {
  padding: 1rem;
}

// Section Header Styling
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;

  .section-title {
    margin: 0;
    color: #2c3e50;
    font-weight: 700;
    font-size: 1.75rem;
  }

  .games-count {
    background: linear-gradient(135deg, #007bff, #0056b3);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.9rem;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
    margin-left: 2rem;
  }
}

// Games Grid Layout
.games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  padding: 0;

  // Force 2 columns on large screens
  @media (min-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  // Allow 3 columns on extra large screens if space permits
  @media (min-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
    max-width: none;
  }
}

// Game Card Styling
.game-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: #007bff;
  }

  .game-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-bottom: 1px solid #dee2e6;
  }

  .game-type-badge {
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &.friendly {
      background: #17a2b8;
      color: white;
    }

    &.league {
      background: #28a745;
      color: white;
    }

    &.cup {
      background: #ffc107;
      color: #212529;
    }

    &.tournament {
      background: #6f42c1;
      color: white;
    }

    &.default {
      background: #6c757d;
      color: white;
    }
  }

  .game-status-indicator {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;

    &.upcoming {
      background: #fff3cd;
      color: #856404;
    }

    &.completed {
      background: #d4edda;
      color: #155724;
    }

    &.in-progress {
      background: #cce7ff;
      color: #004085;
    }
  }
}

// Match Details Styling
.match-details {
  padding: 1.5rem 1rem 1rem;

  .teams-matchup {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    gap: 1rem;

    .team {
      font-weight: 700;
      font-size: 1.1rem;
      color: #2c3e50;
      text-align: center;
      flex: 1;
      padding: 0.5rem;
      background: #f8f9fa;
      border-radius: 8px;
      transition: all 0.2s ease;

      &:hover {
        background: #e9ecef;
      }
    }

    .vs-separator {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 50px;
      height: 50px;
      background: linear-gradient(135deg, #007bff, #0056b3);
      border-radius: 50%;
      color: white;
      font-weight: bold;
      box-shadow: 0 3px 6px rgba(0, 123, 255, 0.3);

      .vs-text {
        font-size: 0.9rem;
        letter-spacing: 1px;
      }
    }
  }

  .teams-result {
    text-align: center;
    margin-bottom: 1rem;

    .result-display {
      font-weight: 700;
      font-size: 1.2rem;
      color: #28a745;
      padding: 1rem;
      background: linear-gradient(135deg, #d4edda, #c3e6cb);
      border-radius: 8px;
      border: 2px solid #28a745;
    }
  }

  .game-info {
    display: flex;
    justify-content: space-around;
    gap: 1rem;
    margin-top: 1rem;

    .info-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: #f8f9fa;
      border-radius: 20px;
      font-size: 0.9rem;
      color: #495057;
      border: 1px solid #e9ecef;

      i {
        color: #007bff;
      }

      span {
        font-weight: 500;
      }
    }
  }
}

// Game Card Footer
.game-card-footer {
  padding: 1rem;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;

  .reports-button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: linear-gradient(135deg, #007bff, #0056b3);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, #0056b3, #004085);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
    }

    &:active {
      transform: translateY(0);
    }

    i {
      font-size: 1rem;
    }
  }
}

// Empty State Styling
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px dashed #dee2e6;

  .empty-state-icon {
    margin-bottom: 1.5rem;

    i {
      font-size: 4rem;
      color: #6c757d;
    }
  }

  .empty-state-content {
    h3 {
      color: #495057;
      margin-bottom: 0.5rem;
      font-weight: 600;
    }

    p {
      color: #6c757d;
      margin: 0;
      font-size: 1.1rem;
    }
  }
}

// Team Select Wrapper Enhancement
.team-select-wrapper {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;

  label {
    display: block;
    margin-bottom: 0.5rem;
    color: #2c3e50;
    font-weight: 600;
    font-size: 1rem;
  }

  select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    font-size: 1rem;
    background: white;
    color: #495057;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }

    &:hover {
      border-color: #007bff;
    }
  }
}

// Date Picker Wrapper Styling
.date-picker-wrapper {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;

  label {
    display: block;
    margin-bottom: 0.5rem;
    color: #2c3e50;
    font-weight: 600;
    font-size: 1rem;
  }

  .date-helper-text {
    margin-top: 0.5rem;
    margin-bottom: 0;
    font-size: 0.875rem;
    color: #6c757d;
    font-style: italic;
  }
}

// Report Status Components (Enhanced)
span.report-status {
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.complete {
    background: linear-gradient(135deg, #d4edda, #c3e6cb);
    color: #155724;
    border: 1px solid #28a745;
  }

  &.incomplete {
    background: linear-gradient(135deg, #fff3cd, #ffeaa7);
    color: #856404;
    border: 1px solid #ffc107;
  }
}

.player-item {
  display: flex;
  align-items: start;
  padding: 1rem 1.5rem;
  margin-bottom: 1rem;
  border-radius: 12px;
  gap: 1rem;
  flex-wrap: wrap;
  transition: all 0.3s ease;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.complete {
    background: linear-gradient(135deg, #d4edda, #c3e6cb);
    border-left: 6px solid #28a745;
  }

  &.incomplete {
    background: linear-gradient(135deg, #fff3cd, #ffeaa7);
    border-left: 6px solid #ffc107;
  }

  &.missing {
    background: linear-gradient(135deg, #f8d7da, #f5c6cb);
    border-left: 6px solid #dc3545;
  }

  .player-name {
    font-weight: 700;
    color: #2c3e50;
    width: 100%;
    font-size: 1.1rem;
  }

  .report-status {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.9rem;

    i {
      font-size: 1.1rem;
    }
  }
}

// Report Category Headers
.report-category {
  margin-bottom: 2rem;

  h5 {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 1rem;
    padding: 1rem;
    border-radius: 8px;

    i {
      font-size: 1.5rem;
    }
  }

  &.complete-reports h5 {
    background: linear-gradient(135deg, #d4edda, #c3e6cb);
    color: #155724;
  }

  &.incomplete-reports h5 {
    background: linear-gradient(135deg, #fff3cd, #ffeaa7);
    color: #856404;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
}

// Responsive Design
@media (max-width: 768px) {
  .games-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .section-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;

    .games-count {
      text-align: center;
    }
  }

  .match-details .teams-matchup {
    flex-direction: column;
    gap: 0.5rem;

    .vs-separator {
      width: 40px;
      height: 40px;
    }
  }

  .match-details .game-info {
    flex-direction: column;
    gap: 0.5rem;
  }

  .player-item {
    padding: 1rem;

    .player-name {
      margin-bottom: 0.5rem;
    }
  }
}

// Loading and Animation Enhancements
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.game-card {
  animation: fadeInUp 0.6s ease forwards;
}

.game-card:nth-child(even) {
  animation-delay: 0.1s;
}

.game-card:nth-child(3n) {
  animation-delay: 0.2s;
}

// Modal Tabs Styling
.modal-tabs-wrapper {
  margin-top: 1.5rem;

  .modal-tabs {
    display: flex;
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
    border-bottom: 2px solid #e9ecef;
    background: #f8f9fa;
    border-radius: 8px 8px 0 0;
    overflow: hidden;

    .modal-tab-item {
      flex: 1;

      .modal-tab-link {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 1rem 1.5rem;
        text-decoration: none;
        color: #6c757d;
        background: transparent;
        border-bottom: 3px solid transparent;
        transition: all 0.3s ease;
        font-weight: 600;
        position: relative;

        i {
          font-size: 1.1rem;
        }

        .tab-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 700;
          min-width: 24px;
          text-align: center;

          &.complete {
            background: #28a745;
            color: white;
          }

          &.incomplete {
            background: #ffc107;
            color: #212529;
          }
        }

        &:hover {
          color: #495057;
          background: #e9ecef;
        }

        &.active {
          color: #007bff;
          background: white;
          border-bottom-color: #007bff;

          &:hover {
            color: #007bff;
            background: white;
          }

          .tab-badge {
            &.complete {
              background: #155724;
            }

            &.incomplete {
              background: #856404;
              color: white;
            }
          }
        }
      }
    }
  }

  .modal-tab-content {
    min-height: 300px;

    .tab-pane {
      animation: fadeInUp 0.4s ease forwards;
    }

    .tab-empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem 2rem;
      text-align: center;
      background: #f8f9fa;
      border-radius: 12px;
      border: 2px dashed #dee2e6;

      .empty-state-icon {
        margin-bottom: 1rem;

        i {
          font-size: 3rem;
          color: #6c757d;
        }
      }

      .empty-state-content {
        h4 {
          color: #495057;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        p {
          color: #6c757d;
          margin: 0;
          font-size: 1rem;
        }
      }
    }
  }
}

// Enhanced loading state for modal
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;

  .spinner-border {
    width: 3rem;
    height: 3rem;
    margin-bottom: 1rem;
  }

  p {
    color: #6c757d;
    margin: 0;
    font-size: 1.1rem;
  }
}

// Match Header Section Styling
.match-header-section {
  background: linear-gradient(135deg, #2c3e50, #3d566e);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: white;

  .match-header-teams {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    margin-bottom: 1rem;

    .match-header-team {
      flex: 1;
      text-align: center;

      .team-name {
        font-size: 1.25rem;
        font-weight: 700;
        display: block;
      }

      &.home {
        text-align: right;
      }

      &.away {
        text-align: left;
      }
    }

    .match-header-score {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;

      .score-display {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        background: rgba(255, 255, 255, 0.15);
        padding: 0.75rem 1.5rem;
        border-radius: 8px;

        .score {
          font-size: 2rem;
          font-weight: 800;
          min-width: 40px;
          text-align: center;
        }

        .score-separator {
          font-size: 1.5rem;
          color: rgba(255, 255, 255, 0.7);
        }
      }

      .match-result-badge {
        padding: 0.25rem 0.75rem;
        border-radius: 20px;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;

        &.win {
          background: #28a745;
          color: white;
        }

        &.loss {
          background: #dc3545;
          color: white;
        }

        &.draw {
          background: #ffc107;
          color: #212529;
        }
      }
    }

    .match-header-vs {
      background: rgba(255, 255, 255, 0.2);
      width: 50px;
      height: 50px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;

      .vs-text {
        font-size: 0.9rem;
        font-weight: 700;
      }
    }
  }

  .goal-scorers-section {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    margin-bottom: 1rem;
    font-size: 0.9rem;

    .goal-scorers {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex: 1;

      i {
        color: #ffc107;
        font-size: 0.85rem;
      }

      &.home-scorers {
        justify-content: flex-start;
      }

      &.away-scorers {
        justify-content: flex-end;
        text-align: right;
      }
    }
  }

  .match-info-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(255, 255, 255, 0.2);

    .match-info-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      font-size: 0.85rem;

      i {
        color: #007bff;
      }
    }
  }
}

// Team Sheet Section Styling
.team-sheet-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  overflow: hidden;

  .team-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, #e9ecef, #dee2e6);
    }

    h4 {
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 1.1rem;
      font-weight: 600;
      color: #2c3e50;

      i {
        color: #007bff;
      }

      .player-count-badge {
        background: #007bff;
        color: white;
        padding: 0.25rem 0.75rem;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 600;
      }
    }

    .toggle-icon {
      font-size: 1.25rem;
      color: #6c757d;
      transition: transform 0.3s ease;
    }
  }

  .team-sheet-content {
    padding: 1.5rem;
    border-top: 1px solid #e9ecef;
  }

  .team-sheet-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }

  .team-sheet-player {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
    transition: all 0.2s ease;

    &:hover {
      background: #e9ecef;
      transform: translateY(-2px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .player-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      overflow: hidden;
      background: #dee2e6;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      i {
        font-size: 2rem;
        color: #6c757d;
      }
    }

    .player-info {
      display: flex;
      flex-direction: column;
      min-width: 0;

      .player-name {
        font-weight: 600;
        color: #2c3e50;
        font-size: 0.95rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .player-position {
        font-size: 0.8rem;
        color: #6c757d;
      }
    }
  }

  .team-sheet-empty {
    text-align: center;
    padding: 2rem;
    color: #6c757d;

    i {
      font-size: 2.5rem;
      margin-bottom: 0.5rem;
      display: block;
    }

    p {
      margin: 0;
    }
  }
}

// Slide transition for collapsible
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
}

// Responsive adjustments for match header
@media (max-width: 768px) {
  .match-header-section {
    padding: 1rem;

    .match-header-teams {
      flex-direction: column;
      gap: 1rem;

      .match-header-team {
        text-align: center !important;

        .team-name {
          font-size: 1.1rem;
        }
      }

      .match-header-score .score-display .score {
        font-size: 1.5rem;
      }
    }

    .goal-scorers-section {
      flex-direction: column;
      gap: 0.5rem;

      .goal-scorers {
        justify-content: center !important;
        text-align: center !important;
      }
    }

    .match-info-row {
      flex-direction: column;
      align-items: center;
    }
  }

  .team-sheet-section .team-sheet-grid {
    grid-template-columns: 1fr;
  }
}

/* Coach Game Performance Modal Styles */
.coach-modal-player-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  margin-bottom: 1.5rem;

  .player-avatar-small {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #007bff;
  }

  .player-info-text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .player-name-modal {
      font-weight: 700;
      font-size: 1.1rem;
      color: #2c3e50;
    }

    .match-info-modal {
      font-size: 0.9rem;
      color: #6c757d;
    }
  }
}

.coach-stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.card-checkboxes {
  display: flex;
  gap: 1rem;
  padding-top: 0.5rem;

  .yellow-card-checkbox {
    .card-icon.yellow {
      font-size: 1.2rem;
    }
  }

  .red-card-checkbox {
    .card-icon.red {
      font-size: 1.2rem;
    }
  }
}

.form__item {
  margin-bottom: 1rem;

  .form__label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #495057;
  }
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

// Eyeball Section
.eyeball-section {
  margin-bottom: 1.5rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;

  .eyeball-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: #f8f9fa;
    cursor: pointer;

    h4 {
      margin: 0;
      font-size: 1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  .eyeball-section-content {
    padding: 1rem;
  }

  .eyeball-video-wrapper {
    margin-bottom: 1rem;
  }

  .eyeball-team-lines {
    margin-top: 1rem;

    .table-sm {
      font-size: 0.85rem;

      th, td {
        padding: 0.3rem 0.5rem;
      }
    }

    h5 {
      font-size: 0.9rem;
      font-weight: 600;
      margin: 0.75rem 0 0.25rem;
    }
  }

  .eyeball-import-section {
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
  }
}

.eyeball-import-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1.25rem;
  background: #0d6efd;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(13, 110, 253, 0.15), 0 2px 8px rgba(13, 110, 253, 0.12);
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
  font-family: inherit;
  text-align: left;
  line-height: 1.25;

  > i,
  > .spinner-border {
    font-size: 1.125rem;
    flex-shrink: 0;
  }

  .btn-text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .btn-title {
    font-size: 0.9375rem;
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  .btn-subtitle {
    font-size: 0.75rem;
    font-weight: 400;
    opacity: 0.85;
    letter-spacing: 0;
  }

  &:hover:not(:disabled) {
    background: #0b5ed7;
    box-shadow: 0 2px 4px rgba(13, 110, 253, 0.18), 0 4px 12px rgba(13, 110, 253, 0.18);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(13, 110, 253, 0.15);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}
</style>
